import flatten from 'flat';
import { compose, mergeWith, omit, omitBy } from 'lodash/fp';

import {
  ContainerPacking,
  CreateCustomerInput,
  CreateLogisticsInput,
  CreateRequestForQuotationInput,
  CreateUserInput,
} from '#/__generated__/graphql/graphql';
import { ProfileType } from '#/hooks';
import {
  CUSTOMER_CANCELLATION_REASON,
  TRANSPORTER_CANCELLATION_REASON,
} from '#/constants/cancellation-reason-list';

export function sanitizeData<T, U>(initialValues: T, values: U, paths: string[] = []): U {
  const _sanitizeData = compose(
    mergeWith((o, s) => (s === null ? o : s))({ ...initialValues }),
    omitBy((_, k) => k.includes('__typename')),
  );

  return omit(paths)(
    flatten.unflatten(_sanitizeData(flatten(values)), {
      object: false,
    }),
  ) as unknown as U;
}

export function sanitizeFormData<T>(values: T): T {
  const _sanitizeData = compose(omitBy((v, k) => k.includes('__typename') || v === ''));
  return flatten.unflatten(_sanitizeData(flatten(values)), { object: false });
}

export type CustomerFormData = Omit<CreateCustomerInput, 'areasServed'> & {
  areasServed: string[];
};
export function sanitizeCustomerFormData(values: CustomerFormData): CreateCustomerInput {
  const formData = sanitizeFormData({
    ...values,
    areasServed: values.areasServed.map((val) => ({
      address: { addressRegion: val },
    })),
  });
  if (formData.businessCategoryId !== 'other') {
    formData.otherBusinessCategory = undefined;
  } else {
    formData.businessCategoryId = undefined;
  }
  return formData;
}

export type LogisticsFormData = Omit<CreateLogisticsInput, 'areasServed'> & {
  areasServed: string[];
};
export function sanitizeLogisticsFormData(values: LogisticsFormData): CreateLogisticsInput {
  const formData = sanitizeFormData({
    ...values,
    areasServed: values.areasServed.map((val) => ({
      address: { addressRegion: val },
    })),
  });
  return formData;
}

export function sanitizeOrderCancellationFormData(values: any, userType: ProfileType) {
  const formData = sanitizeFormData(values);
  if (formData.selection !== '99') {
    const item =
      userType === 'Customer'
        ? CUSTOMER_CANCELLATION_REASON.find((x) => x.id === formData.selection)
        : TRANSPORTER_CANCELLATION_REASON.find((x) => x.id === formData.selection);
    formData.remark = item?.reason;
  }
  return formData;
}

export function sanitizeRFQFormData(
  values: CreateRequestForQuotationInput,
): CreateRequestForQuotationInput {
  const formData = sanitizeFormData(values);
  if (!!formData.attachments) {
    const isFileList =
      formData.attachments instanceof FileList || Array.isArray(formData.attachments);
    formData.attachments = (
      isFileList ? Array.from(formData.attachments) : [formData.attachments]
    ).map((attachment) => ({
      contentFile: attachment,
    }));
  }
  if (!!formData.cargoDetails.cargoWeight) {
    formData.cargoDetails.cargoWeight.unitCode = 'KGM';
  }
  formData.deliveryTime = new Date(values.deliveryTime).toISOString();
  formData.pickupTime = new Date(values.pickupTime).toISOString();
  if (
    !!formData.preferredContainerPacking &&
    formData.preferredContainerPacking === ContainerPacking.LessThanContainerLoad
  ) {
    formData.preferredVehicle = { name: '' }; // send blank to api
  }

  return formData;
}

export function sanitizeCreateUserFormData(values: any): CreateUserInput {
  const formData: CreateUserInput = sanitizeFormData({
    email: values.email,
    password: values.password,
    roles: values.roles,
    username: values.username,
  });
  return formData;
}
